import { useMutation, useQueryClient } from "@tanstack/react-query";
import ApiHttp from "byzantine/src/ApiHttp";

import { RECEIPT_DETAIL_QUERY_KEY } from "./useReceipt";
import { RECEIPTS_POLL_KEY, RECEIPTS_QUERY_KEY } from "./useReceiptsList";

import type { ReceiptDetail } from "./useReceipt";
import type { ReceiptListRow, ReceiptsListResponse } from "./useReceiptsList";

export interface AcceptMatchError extends Error {
  isConflict: boolean;
  status?: number;
}

export interface AcceptMatchVariables {
  suggestionId: number;
  receiptUuid?: string;
}

export interface UseAcceptMatchOptions {
  onSuccess?: (updated: ReceiptDetail) => void;
  onError?: (error: AcceptMatchError) => void;
}

interface AcceptMatchContext {
  prev: Array<[readonly unknown[], unknown]>;
}

type AcceptMatchInput = number | AcceptMatchVariables;

const normalize = (input: AcceptMatchInput): AcceptMatchVariables =>
  typeof input === "number" ? { suggestionId: input } : input;

const removeRowFromList = (
  list: ReceiptsListResponse,
  receiptUuid: string
): ReceiptsListResponse => {
  const removed = list.receipts.find(
    (row: ReceiptListRow) => row.uuid === receiptUuid
  );
  if (!removed) return list;
  return {
    ...list,
    receipts: list.receipts.filter((row) => row.uuid !== receiptUuid),
    meta: {
      ...list.meta,
      total: Math.max(0, list.meta.total - 1),
      counts: {
        ...list.meta.counts,
        pending: Math.max(0, list.meta.counts.pending - 1),
        matched: list.meta.counts.matched + 1,
      },
    },
  };
};

const extractStatus = (err: unknown): number | undefined => {
  if (typeof err !== "object" || err === null) return undefined;
  const { response } = err as { response?: { status?: unknown } };
  if (!response || typeof response.status !== "number") return undefined;
  return response.status;
};

const extractMessage = (err: unknown): string => {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  return "Accept match failed";
};

const toAcceptMatchError = (err: unknown): AcceptMatchError => {
  const status = extractStatus(err);
  const wrapped = new Error(extractMessage(err)) as AcceptMatchError;
  wrapped.isConflict = status === 409;
  wrapped.status = status;
  return wrapped;
};

export const useAcceptMatch = (options: UseAcceptMatchOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation<
    ReceiptDetail,
    AcceptMatchError,
    AcceptMatchInput,
    AcceptMatchContext
  >({
    mutationFn: async (input) => {
      const { suggestionId } = normalize(input);
      try {
        // rawResponse: true so the thrown error exposes response.status for the
        // 409 branch instead of being unwrapped to just response.data.
        const response = await ApiHttp.fetch<ReceiptDetail>(
          `receipt-transaction-suggestions/${suggestionId}/`,
          { method: "PATCH", rawResponse: true },
          { status: "accepted" }
        );
        return (response as unknown as { data: ReceiptDetail }).data;
      } catch (err) {
        throw toAcceptMatchError(err);
      }
    },
    onMutate: async (input) => {
      const { receiptUuid } = normalize(input);
      // Scope to bucket-list queries only — the upload-polling query
      // ([RECEIPTS_QUERY_KEY, "poll", uuid]) tracks its own visibility and
      // breaks if we mutate counts/rows out from under it.
      const isListQuery = (queryKey: readonly unknown[]): boolean =>
        queryKey[0] === RECEIPTS_QUERY_KEY && queryKey[1] !== RECEIPTS_POLL_KEY;
      await queryClient.cancelQueries({
        predicate: (q) => isListQuery(q.queryKey),
      });
      const prev = queryClient.getQueriesData({
        predicate: (q) => isListQuery(q.queryKey),
      });

      if (receiptUuid) {
        prev.forEach(([key, data]) => {
          if (!data) return;
          const next = removeRowFromList(
            data as ReceiptsListResponse,
            receiptUuid
          );
          queryClient.setQueryData(key, next);
        });
      }

      return { prev };
    },
    onError: (err, _vars, context) => {
      context?.prev.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
      options.onError?.(err);
    },
    onSuccess: (updated) => {
      queryClient.invalidateQueries({
        predicate: (q) =>
          q.queryKey[0] === RECEIPTS_QUERY_KEY &&
          q.queryKey[1] !== RECEIPTS_POLL_KEY,
      });
      queryClient.invalidateQueries({
        queryKey: [RECEIPT_DETAIL_QUERY_KEY, updated.uuid],
      });
      options.onSuccess?.(updated);
    },
  });
};
